'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Tab, TabList, Tabs } from '@ironhack/design-system2/components/tab';
import { Text } from '@ironhack/design-system2/components/text';
import {
  anyPass,
  isEmpty,
  isNil,
  join,
  negate,
  pathSatisfies,
  propSatisfies,
  reject,
  startsWith,
  unless,
  update,
} from 'ramda';
import {
  Children,
  type ReactElement,
  type RefObject,
  cloneElement,
  createRef,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CancelWrap } from '@/app/components/server/cancel-wrap';
import {
  ModuleWrapper,
  WRAPPER_PADDING,
} from '@/app/components/server/module-wrapper';
import { useIsStuck } from './use-is-stuck';
import {
  CLICK_MARGIN,
  type Child,
  type ChildProps,
  NAV_HEIGHT,
  type NavigationChild,
  useScrollingIndexUpdate,
} from './use-scrolling-index-update';

type MaybeChild = Child | null | undefined;

type Padding = number | null;

type Props = {
  // The elements that could appear as tabs in the secondary navigation. They
  // need to have a title prop and can't be async functions.
  children: MaybeChild[];
  navTitle: string | undefined;
} & BoxProps;

// NavigationTabs serves as a page's secondary navigation.
export const NavigationTabs = (props: Props): ReactElement | null => {
  const { navTitle, children, ...boxProps } = props;

  const navRef = useRef<HTMLDivElement>(null);
  const [navIndex, setNavIndex] = useState<number>(0);

  const { isStuck, top } = useIsStuck(navRef);

  // Memoize to not recreate extra refs for no reason
  const childrenWithRef = useMemo((): Child[] => {
    const mappedChildren = Children.map(
      children,
      (child: MaybeChild, index: number): MaybeChild => {
        if (isNil(child)) return child;

        const {
          props: { title },
        } = child;

        if (isNil(title) || isEmpty(title) || title.startsWith('*')) {
          return child;
        }

        const extraMargin = index === 0 ? 0 : CLICK_MARGIN;
        const cloneProps = {
          ref: createRef<HTMLDivElement>(),
          scrollMarginTop: `${NAV_HEIGHT + extraMargin}px`,
        };

        return cloneElement<ChildProps>(child, cloneProps);
      }
    );

    return mappedChildren ?? [];
  }, [children]);

  const navigationTabs = reject(
    anyPass([
      propSatisfies(isNil, 'ref'),
      pathSatisfies(isEmpty, ['props', 'title']),
      pathSatisfies(startsWith('*'), ['props', 'title']),
    ]),
    childrenWithRef
  ) as NavigationChild[];

  // Memoize to not recreate extra refs for no reason
  const tabRefs: Array<RefObject<HTMLButtonElement>> = useMemo(
    () => navigationTabs.map(() => createRef<HTMLButtonElement>()),
    [navigationTabs]
  );

  useScrollingIndexUpdate(navigationTabs, (index: number): void => {
    setNavIndex(index);

    // Scroll horizontally on secondary nav for this tab
    tabRefs[index].current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest', // scroll horizontally only
    });
  });

  if (!navTitle) return <>{children}</>;

  // Extend the scrolling space of secondary nav to the right on large screens
  const cancelPaddingDesktop = update<Padding>(0, null, WRAPPER_PADDING);
  const cancelMarginDesktop = cancelPaddingDesktop.map(
    unless(isNil, negate) as (p: Padding) => Padding
  );

  const scrollToRef = ({ ref }: NavigationChild): void => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <ModuleWrapper
        moduleFormat={{
          background: 'white',
          spacingBottom: '24',
          spacingTop: '32',
        }}
        outerProps={{
          as: 'nav',
          boxShadow: isStuck
            ? join(',', [
                '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                '0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              ])
            : 'unset',
          height: `${NAV_HEIGHT}px`,
          position: 'sticky',
          top,
          zIndex: '100',
          ...boxProps,
        }}
        ref={navRef}
      >
        <CancelWrap
          alignItems="start"
          as={Flex}
          below="md"
          overflowX={['auto', null, 'visible']}
          width={[null, null, '100%']}
          withPadding
        >
          <Text
            borderBottom="4px"
            borderColor="transparent"
            mr={3}
            pb={0.5}
            textStyle="l"
          >
            {navTitle}
          </Text>
          <Tabs
            index={navIndex}
            mr={cancelMarginDesktop}
            onChange={setNavIndex}
            overflowX={[null, null, 'auto']}
          >
            <TabList gap={[3, null, 5]} pr={cancelPaddingDesktop}>
              {navigationTabs.map((tab, index) => (
                <Tab
                  key={tab.props.title}
                  onClick={() => scrollToRef(tab)}
                  ref={tabRefs[index]}
                  scrollMargin={`0 ${CLICK_MARGIN}px`}
                >
                  {tab.props.title}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </CancelWrap>
      </ModuleWrapper>

      {childrenWithRef}
    </>
  );
};
